/**
 * ProductRegisterContainer
 * --
 */
import { ProductProvider } from './context';
import ProductRegisterPresenter from './ProductRegisterPresenter';
import { ProductImageProvider } from './context/ProductImageProvider';
import {
  ProductApi,
  FileApi,
  ProductFileApi,
  ProductTagApi,
  ProductPolicyApi,
  MapProductPolicyApi,
} from 'api';
import MessageAlert from 'utils';
import { ProductDeliveryProvider } from './context/ProductDeliveryProvider';
import { ProductOptionProvider } from './context/ProductOptionProvider';
import { ProductDetailInformationProvider } from './context/ProductDetailInformationProvider';
import { ProductPaymentAdditionalInformationProvider } from './context/ProductPaymentAdditionalInformationProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { ProductTagProvider } from './context/ProductTagProvider';

/**----------------------------------------
 * [Component] 총관리자: 상품등록 - 컨테이너
 * ----------------------------------------
 */
const ProductRegisterContainer = () => {
  /* ===== INITIAL ===== */
  const navigate = useNavigate();
  const location = useLocation();

  /* ===== STATE ===== */
  const [defaultData, setDefaultData] = useState(null);
  // console.log('defaultData =====>', defaultData);

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**
   * 렌탈 옵션 기본값 확인
   * --
   * @param {object} data
   * @returns
   */
  function ensureDefaultValues(data) {
    const keys = [
      'contract_date',
      'advance_price',
      'management',
      'additional_cost',
    ];

    // 최상위 배열들에 대해 처리
    keys.forEach((key) => {
      if (!Array.isArray(data[key]) || data[key].length === 0) {
        throw new Error(`{key} 배열이 비어있습니다.`);
      }
      // 해당 배열에 default:true인 항목이 없으면, 0번째 항목을 default:true로 설정
      if (!data[key].some((item) => item.default === true)) {
        data[key][0].default = true;
      }
    });

    // contract_date 내의 mandatory_usage_date 배열에 대해 처리
    if (Array.isArray(data.contract_date)) {
      data.contract_date.forEach((item, index) => {
        if (
          item.mandatory_usage_date &&
          Array.isArray(item.mandatory_usage_date) &&
          item.mandatory_usage_date.length > 0
        ) {
          if (
            !item.mandatory_usage_date.some((nested) => nested.default === true)
          ) {
            item.mandatory_usage_date[0].default = true;
          }
        }
      });
    }

    return data;
  }
  // 공통 숫자 유효성 검사
  function isValidNumber(value) {
    return typeof value === 'number' && value >= 0;
  }
  /**
   * 유효성 검사
   * @param {*} data
   * @returns
   */
  const handleValueCheck = (data) => {
    const {
      product_name,
      product_category_id,
      num,
      price,
      type,
      is_present,
      present_information,
      forwarding_date,
    } = data;
    if (!product_name) {
      MessageAlert.info('상품명을 입력해주세요.');
      return false;
    }
    if (!product_category_id) {
      MessageAlert.info('상품분류를 선택해주세요.');
      return false;
    }
    if (!isValidNumber(num)) {
      MessageAlert.info('상품 수량은 0이상 입력해주세요.');
      return false;
    }
    if (!isValidNumber(price)) {
      MessageAlert.info('상품금액은 0이상 입력해주세요.');
      return false;
    }
    if (!isValidNumber(forwarding_date)) {
      MessageAlert.info('출고일은 0이상 입력해주세요.');
      return false;
    }

    if (type === 'DELIVERY' && is_present === 'Y') {
      const { present_expired_date, present_postponed_deadline } =
        present_information;
      if (!isValidNumber(present_expired_date)) {
        MessageAlert.info('선물 만료일을 입력해주세요.');
        return false;
      }
      if (!isValidNumber(present_postponed_deadline)) {
        MessageAlert.info('최대 연장 횟수를 입력해주세요.');
        return false;
      }
    }

    return true;
  };

  /**--------------------
   *
   * --------------------
   */
  const handleSubmit = async (
    productId,
    salesProductInformation,
    productImageInformation,
    productDeliveryInformation,
    productOptionInformation,
    productDetailInformation,
    productTagsInformation,
    productPaymentAdditionalInformation,
  ) => {
    // console.log('#1 productDetailInformation: ', productDetailInformation);
    // 상품유형
    const { type, regular_information, rental_information } =
      salesProductInformation;
    // 스토어픽업여부
    const { is_store_pickup, receive_ids } = productDeliveryInformation;
    if (
      !handleValueCheck({
        ...salesProductInformation,
        ...productDeliveryInformation,
      })
    )
      return;
    // console.log('====== Log: start =====');
    // console.log('salesProductInformation: ', salesProductInformation);
    // console.log('productImageInformation: ', productImageInformation);
    // console.log('productDeliveryInformation: ', productDeliveryInformation);
    // console.log('productOptionInformation: ', productOptionInformation);
    // console.log('productDetailInformation: ', productDetailInformation);
    // console.log('productTagsInformation: ', productTagsInformation);
    // console.log(
    //   'productPaymentAdditionalInformation: ',
    //   productPaymentAdditionalInformation
    // );
    // console.log('====== Log: end =====');
    // productDetailInformation.detail_info.forEach((item) => {
    //   productDetail[item.title] = item.content;
    // });
    const newProductDetail = productDetailInformation.detail_info.reduce(
      (acc, cur) => {
        const [title, children] = cur;

        acc[title] = Object.assign({}, ...children);

        return acc;
      },
      {},
    );
    const newPaymentAdditionalInfo =
      productPaymentAdditionalInformation.payment_additional_info?.reduce(
        (acc, arr) => {
          acc[arr.key] = arr.value; // key를 사용하여 value를 할당
          return acc;
        },
        {},
      );
    // SECTION
    // 최종 생성될 데이터
    let productInformation = {
      ...salesProductInformation,
      is_present:
        salesProductInformation.type !== 'DELIVERY' &&
        salesProductInformation.type !== 'AMOUNT'
          ? 'N'
          : salesProductInformation.is_present,
      present_information:
        (salesProductInformation.type === 'DELIVERY' ||
          salesProductInformation.type === 'AMOUNT') &&
        salesProductInformation.is_present === 'Y'
          ? salesProductInformation?.present_information
          : null,
      product_option: productOptionInformation.options.length
        ? productOptionInformation.options.map((item, index) => ({
            ...item,
            option_id: index + 1,
            option: item.option.map((detailItem, index) => ({
              option_key: index + 1,
              ...detailItem,
            })),
          }))
        : null,
      product_detail: newProductDetail,
      is_bundle_delivery: productDeliveryInformation.is_bundle_delivery,
      forwarding_location_id: productDeliveryInformation.forwarding_location_id,
      is_store_pickup: productDeliveryInformation.is_store_pickup,
      packaging_method: productDeliveryInformation.packaging_method,
      forwarding_information: productDeliveryInformation.forwarding_information,
      forwarding_date: productDeliveryInformation.forwarding_date,

      receive_ids:
        is_store_pickup === 'Y' && receive_ids?.length > 0 ? receive_ids : null,
      regular_information:
        type === 'SUBSCRIPTION'
          ? {
              ...regular_information,
              max_regular_delivery_count:
                regular_information?.max_regular_delivery_count ?? '0',
            }
          : null,
      rental_information:
        type === 'RENTAL' ? ensureDefaultValues(rental_information) : null,

      payment_additional_information:
        Object.keys(newPaymentAdditionalInfo).length === 0
          ? null
          : newPaymentAdditionalInfo,
    };
    //

    if (typeof productId === 'number') {
      productInformation.product_id = productId;
    }

    // 상품정보 등록 및 수정
    const productResponse = !productId
      ? await ProductApi.createProduct(productInformation)
      : await ProductApi.updateProduct(productInformation);
    //
    if (productResponse.status === 200) {
      const { product_id } = productResponse.data;

      try {
        if (salesProductInformation?.product_policy) {
          const { product_policy } = salesProductInformation;
          const productPolicyId = product_policy?.product_policy_id ?? null;
          const mapProductPolicyId =
            product_policy?.map_product_policy_id ?? null;
          const newProductPolicy = {
            product_policy_id: productPolicyId,
            commission_rate: product_policy.commission_rate ?? 0,
            discount_rate: product_policy.discount_rate ?? 0,
            discount_type: product_policy.discount_type,
            is_enable: product_policy.is_enable,
            is_show: product_policy.is_show,
            max_discount_price: product_policy.max_discount_price ?? 0,
            policy_status: product_policy.policy_status,
            policy_type: product_policy.policy_type,
            reserve_rate: product_policy.reserve_rate ?? 0,
            reserve_type: product_policy.reserve_type,
            share_ratio: product_policy.share_ratio,
          };
          //정책 생성
          const productPolicyResponse = !productPolicyId
            ? await ProductPolicyApi.createProductPolicy(newProductPolicy)
            : await ProductPolicyApi.updateProductPolicy(newProductPolicy);

          if (
            (productPolicyResponse.status === 200 && !productPolicyId) ||
            !mapProductPolicyId
          ) {
            //이미 존재 하지 않을 때만 맵핑정책 생성
            const { product_policy_id } = productPolicyResponse.data;
            await MapProductPolicyApi.createMapProductPolicy({
              product_policy_id: product_policy_id,
              product_id: product_id,
              policy_status: 'POSSIBLE',
              is_enable: 'Y',
            });
          }
        }
      } catch (e) {
        console.log(e);
        MessageAlert.warning('할인금 설정에 실패하였습니다.');
      }

      //////////////
      // 상품 수정페이지의 경우 상품 이미지를 전부 삭제 후
      // 상품 이미지를 새로 추가 한다.
      if (defaultData?.product_file) {
        try {
          const removeList = defaultData.product_file.reduce((acc, cur) => {
            const { product_file_id, type } = cur;

            let isSame = false;
            if (type === 'THUMBNAIL') {
              isSame =
                productImageInformation.thumbnail.uid === product_file_id;
            } else {
              isSame = productImageInformation.productImages.some(
                (image) => image.uid === product_file_id,
              );
            }

            if (!isSame) acc.push(cur);

            return acc;
          }, []);

          // 기존에 등록된 상품 이미지를 삭제 처리
          // const promises = defaultData.product_file.map(async (productFile) => {
          //   await ProductFileApi.deleteProductFile(productFile.product_file_id);
          // });

          const promises = removeList.map(async (productFile) => {
            await ProductFileApi.deleteProductFile(productFile.product_file_id);
          });

          // 삭제 처리 완료
          await Promise.all(promises);

          //
          if (
            productImageInformation.thumbnail &&
            productImageInformation.thumbnail.file
          ) {
            // 상품 썸네일 등록
            const thumbnailFileResponse = await FileApi.uploadFile({
              file: productImageInformation.thumbnail.file,
              file_name: nanoid(),
            });

            // 상품 이미지 재 등록
            await ProductFileApi.createProductFile({
              product_id: product_id,
              file_name: productImageInformation.thumbnail.file.name,
              file_url: thumbnailFileResponse.data,
              type: 'THUMBNAIL',
            });
          }

          //
          if (
            Array.isArray(productImageInformation.productImages) &&
            productImageInformation.productImages.length > 0
          ) {
            const promise = productImageInformation.productImages.map(
              async (image) => {
                if (!image.file) return;

                const productFileResponse = await FileApi.uploadFile({
                  file: image.file,
                  file_name: nanoid(),
                });

                const res = await ProductFileApi.createProductFile({
                  product_id: product_id,
                  file_name: image.file.name,
                  file_url: productFileResponse.data,
                  type: 'PRODUCT',
                });
                // console.log('res ==============>', res);
              },
            );

            await Promise.all(promise);
          }
        } catch (error) {
          console.log(error);
          MessageAlert.error('상품등록에 실패하였습니다.');
        }
      } else {
        try {
          if (productImageInformation.thumbnail) {
            // 썸네일 이미지 등록
            const thumbnailFileResponse = await FileApi.uploadFile({
              file: productImageInformation.thumbnail.file,
              file_name: nanoid(),
            });

            //
            await ProductFileApi.createProductFile({
              product_id: product_id,
              file_name: productImageInformation.thumbnail.file.name,
              file_url: thumbnailFileResponse.data,
              type: 'THUMBNAIL',
            });
          }

          // 상품 이미지 등록
          if (
            Array.isArray(productImageInformation.productImages) &&
            productImageInformation.productImages.length > 0
          ) {
            const promise = productImageInformation.productImages.map(
              async (image) => {
                //
                const productFileResponse = await FileApi.uploadFile({
                  file: image.file,
                  file_name: nanoid(),
                });

                //
                await ProductFileApi.createProductFile({
                  product_id: product_id,
                  file_name: productImageInformation.thumbnail.file.name,
                  file_url: productFileResponse.data,
                  type: 'PRODUCT',
                });
              },
            );
            //
            await Promise.all(promise);
          }
        } catch (error) {
          console.log(error);
          MessageAlert.error('상품등록에 실패하였습니다.');
          return;
        }
      }

      // 상품 태그 등록
      // 상품 수정페이지일 경우 추가되거나 삭제된 태그 처리
      if (defaultData) {
        try {
          const { tag = [] } = defaultData;
          const { tags = [] } = productTagsInformation;

          const removeTags = tag?.filter(
            (_item) => !tags.some((item) => item.tag_id === _item.tag_id),
          );
          const addTags = tags?.filter(
            (item) => !tag.some((_item) => item.tag_id === _item.tag_id),
          );

          if (addTags.length) {
            await ProductTagApi.bulkCreateProductTags(
              addTags.map((tag) => ({
                product_id: product_id,
                tag_id: tag.tag_id,
              })),
            );
          }

          if (removeTags.length) {
            await ProductTagApi.bulkDeleteProductTags(
              removeTags.map((tag) => ({
                product_tag_id: tag.product_tag_id,
              })),
            );
          }
        } catch (error) {
          console.log(error);
          MessageAlert.error('상품등록에 실패하였습니다.');
          return;
        }

        //
      } else {
        if (Array.isArray(productTagsInformation.tags)) {
          try {
            //
            const { tags } = productTagsInformation;
            //
            await ProductTagApi.bulkCreateProductTags(
              tags.map((tag) => ({
                product_id: product_id,
                tag_id: tag.tag_id,
              })),
            );
          } catch (error) {
            console.log(error);
            MessageAlert.error('상품등록에 실패하였습니다.');
            return;
          }
        }
      }

      MessageAlert.success(
        `${
          !productId
            ? '상품등록에 성공하였습니다.'
            : '상품수정에 성공하였습니다.'
        }`,
      );

      setTimeout(() => {
        navigate(`/product/list`);
      }, 500);
    } else {
      MessageAlert.error('상품등록에 실패하였습니다.');
    }

    // SECTION

    // if (productImageInformation.thumbnail) {
    //   const uploadProductThumbnailImageResponse = await FileApi.uploadFile({
    //     file: productImageInformation.thumbnail.file,
    //     file_name: nanoid()
    //   });

    //   console.log(uploadProductThumbnailImageResponse)
    // }

    // // 상품 이미지 등록
    // console.log(salesProductInformation);
    // console.log(productImageInformation);
  };

  /* ===== HOOKS ===== */
  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    if (location.state) {
      setDefaultData(location.state);
    }
  }, [location.state]);

  /* ===== RENDER ===== */
  // console.log('[ProductRegisterContainer][Render]defaultData: ', defaultData);

  // JSX: start
  return (
    <ProductProvider>
      <ProductImageProvider>
        <ProductOptionProvider>
          <ProductDeliveryProvider>
            <ProductDetailInformationProvider>
              <ProductTagProvider>
                <ProductPaymentAdditionalInformationProvider>
                  <ProductRegisterPresenter
                    defaultData={defaultData}
                    onDefaultData={setDefaultData}
                    onSubmit={handleSubmit}
                  />
                </ProductPaymentAdditionalInformationProvider>
              </ProductTagProvider>
            </ProductDetailInformationProvider>
          </ProductDeliveryProvider>
        </ProductOptionProvider>
      </ProductImageProvider>
    </ProductProvider>
  ); // JSX: end
};

export default ProductRegisterContainer;
